/**
 * Small helpers of the Kafka view (KafkaView and its tabs): naming a topic, which groups consumed a message, the text
 * search of a loaded page, a value pretty or short, and a refresh every few seconds while the page is visible.
 */
import { useEffect, useRef } from 'react';

export const REFRESH_MS = 5000;

/** The key of a topic in the view: its real name when the server gives one, else its name. */
export const topicKey = (t) => (t ? t.topic || t.name : null);

export const groupStates = { Stable: 'is-ok', Empty: '', PreparingRebalance: 'is-warn', CompletingRebalance: 'is-warn', Dead: 'is-warn' };

/** The groups whose committed offset on the message's partition is above it (fresh consumers data first). */
export function consumedByOf(message, consumers) {
  if (!consumers?.groups) { return message.consumedBy || []; }
  return consumers.groups
    .filter((g) => {
      const p = (g.partitions || []).find((x) => x.partition === Number(message.partition));
      return p && p.committed !== null && p.committed !== undefined && Number(message.offset) < p.committed;
    })
    .map((g) => g.groupId);
}

export function prettyValue(message) {
  if (message.encoding === 'base64') { return message.value; }
  try { return JSON.stringify(JSON.parse(message.value), null, 2); } catch { return String(message.value ?? ''); }
}

export const isJson = (message) => {
  if (message.encoding === 'base64' || typeof message.value !== 'string') { return false; }
  try { const v = JSON.parse(message.value); return v !== null && typeof v === 'object'; } catch { return false; }
};

export function shortValue(message) {
  const text = message.encoding === 'base64' ? `base64 · ${message.value?.length || 0} chars` : String(message.value ?? '');
  return text.length > 120 ? `${text.slice(0, 117)}…` : text;
}

/** Whether the message's key, value or headers contain `text` (case-insensitive). */
export function matches(message, text) {
  const needle = String(text || '').trim().toLowerCase();
  if (!needle) { return true; }
  const hay = [message.key, message.value, ...Object.entries(message.headers || {}).flat()].map((v) => String(v ?? '').toLowerCase());
  return hay.some((v) => v.includes(needle));
}

/** Newest first: by time, then offset. */
export const newestFirst = (list) => [...list].sort((a, b) => String(b.timestamp || '').localeCompare(String(a.timestamp || '')) || Number(b.offset) - Number(a.offset) || b.partition - a.partition);

export const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Calls `fn` every `ms` while `on` and the page is visible. */
export function useAutoRefresh(fn, { on = true, ms = REFRESH_MS } = {}) {
  const saved = useRef(fn);
  useEffect(() => { saved.current = fn; }, [fn]);
  useEffect(() => {
    if (!on) { return undefined; }
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') { return; }
      saved.current();
    }, ms);
    return () => clearInterval(id);
  }, [on, ms]);
}
